import React from "react";
import clsx from "clsx";
import { SpaceNotification } from "types/spaceTypes";

interface SpaceNotificationCardProps {
    notification: SpaceNotification;
    onDismiss?: (id: string) => void;
}

export default function SpaceNotificationCard({ 
    notification, 
    onDismiss 
}: SpaceNotificationCardProps) {
    
    const getNotificationStyles = (type: SpaceNotification['type']) => {
        switch (type) {
            case 'join_success':
                return {
                    container: 'bg-white border-l-4 border-l-green-500 border border-gray-200',
                    icon: 'ri-check-circle-fill text-green-500 text-base',
                    iconBg: 'bg-green-50',
                    title: 'text-slate-900',
                    message: 'text-slate-600'
                };
            case 'join_error':
                return {
                    container: 'bg-white border-l-4 border-l-red-500 border border-gray-200',
                    icon: 'ri-close-circle-fill text-red-500 text-base',
                    iconBg: 'bg-red-50',
                    title: 'text-slate-900',
                    message: 'text-slate-600'
                };
            case 'welcome':
                return {
                    container: 'bg-blue-50 border-blue-200',
                    icon: 'ri-information-fill text-blue-600',
                    iconBg: 'bg-blue-100',
                    title: 'text-blue-800',
                    message: 'text-blue-700'
                };
            case 'mention':
                return {
                    container: 'bg-yellow-50 border-yellow-200',
                    icon: 'ri-at-line text-yellow-600',
                    iconBg: 'bg-yellow-100',
                    title: 'text-yellow-800',
                    message: 'text-yellow-700'
                };
            default:
                return {
                    container: 'bg-slate-50 border-slate-200',
                    icon: 'ri-information-line text-slate-600',
                    iconBg: 'bg-slate-100',
                    title: 'text-slate-800',
                    message: 'text-slate-700'
                };
        }
    };

    const styles = getNotificationStyles(notification.type);

    return (
        <div className={clsx(
            'rounded-lg p-4 mb-3 relative shadow-sm max-w-md w-fit',
            styles.container
        )}>
            <div className="flex items-start gap-3">
                {/* Icon with background */}
                <div className={clsx(
                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5',
                    styles.iconBg
                )}>
                    <i className={styles.icon}></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <h4 className={clsx(
                        'text-sm font-medium mb-1',
                        styles.title
                    )}>
                        {notification.title}
                    </h4>
                    
                    <p className={clsx(
                        'text-sm leading-relaxed',
                        styles.message
                    )}>
                        {notification.message}
                    </p>

                    {/* Action button */}
                    {notification.actionable && notification.action && (
                        <button
                            onClick={notification.action.onClick}
                            className={clsx(
                                'mt-3 px-3 py-1.5 text-xs font-medium rounded-md',
                                'transition-colors duration-200',
                                {
                                    'bg-green-600 text-white hover:bg-green-700': notification.type === 'join_success',
                                    'bg-red-600 text-white hover:bg-red-700': notification.type === 'join_error',
                                    'bg-blue-600 text-white hover:bg-blue-700': notification.type === 'welcome',
                                    'bg-yellow-600 text-white hover:bg-yellow-700': notification.type === 'mention',
                                }
                            )}
                        >
                            {notification.action.label}
                        </button>
                    )}
                </div>

                {/* Dismiss button */}
                {notification.dismissible && onDismiss && (
                    <button
                        onClick={() => onDismiss(notification.id)}
                        className="flex-shrink-0 w-5 h-5 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center"
                        aria-label="Dismiss notification"
                    >
                        <i className="ri-close-line text-gray-400 hover:text-gray-600 text-sm"></i>
                    </button>
                )}
            </div>
        </div>
    );
}
